import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../App'
import {
  Heart,
  Activity,
  Moon,
  Zap,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  CheckCircle2,
  Clock,
  Coffee,
  Brain,
  Loader2,
  Link2,
  ChevronRight,
  Bell,
  BellOff,
  Play,
  Pause,
  RotateCcw,
  Shield,
  Smartphone,
  Watch,
  Sparkles,
  Target,
  Flame
} from 'lucide-react'

// Health Score Color Coding
function getHealthColor(score) {
  if (score >= 80) return 'var(--accent-green)'
  if (score >= 60) return 'var(--accent-blue)'
  if (score >= 40) return 'var(--accent-yellow)'
  return 'var(--accent-red)'
}

function getHealthLabel(score) {
  if (score >= 80) return 'Excellent'
  if (score >= 60) return 'Good'
  if (score >= 40) return 'Fair'
  return 'Needs Attention'
}

function getHealthEmoji(score) {
  if (score >= 80) return '🟢'
  if (score >= 60) return '🔵'
  if (score >= 40) return '🟡'
  return '🔴'
}

// Energy Ring Component
function EnergyRing({ value, size = 160 }) {
  const radius = (size - 20) / 2
  const circumference = 2 * Math.PI * radius
  const progress = (value / 100) * circumference
  const color = getHealthColor(value)

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth="12"
        />
        {/* Progress ring */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - progress }}
          transition={{ duration: 1, ease: 'easeOut' }}
          style={{ filter: `drop-shadow(0 0 8px ${color}40)` }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span style={{ fontSize: '2.5rem', fontWeight: 600, color }}>{Math.round(value)}</span>
        <span className="text-tertiary" style={{ fontSize: '0.875rem' }}>Energy Index</span>
      </div>
    </div>
  )
}

// Health Pulse Card (Main Score)
function HealthPulseCard({ wellness, healthScore }) {
  const color = getHealthColor(healthScore)
  const label = getHealthLabel(healthScore)

  return (
    <div className="glass-card" style={{ padding: 'var(--space-xl)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
        <Heart size={20} style={{ color }} className="pulse-icon" />
        <h3>Health Pulse</h3>
        <span style={{
          marginLeft: 'auto',
          padding: 'var(--space-xs) var(--space-sm)',
          background: `${color}20`,
          color,
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.75rem',
          fontWeight: 500
        }}>
          {label}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2xl)' }}>
        <EnergyRing value={healthScore} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <MetricRow
            icon={Activity}
            label="Recovery Score"
            value={wellness?.recovery_score ? `${Math.round(wellness.recovery_score)}%` : '--'}
            color={wellness?.recovery_score >= 67 ? 'var(--accent-green)' : wellness?.recovery_score >= 34 ? 'var(--accent-yellow)' : 'var(--accent-red)'}
          />
          <MetricRow
            icon={Heart}
            label="Resting Heart Rate"
            value={wellness?.resting_heart_rate ? `${Math.round(wellness.resting_heart_rate)} bpm` : '--'}
          />
          <MetricRow
            icon={Activity}
            label="HRV"
            value={wellness?.hrv_rmssd ? `${Math.round(wellness.hrv_rmssd)} ms` : '--'}
          />
          <MetricRow
            icon={Moon}
            label="Sleep"
            value={wellness?.sleep_duration_minutes ? formatSleep(wellness.sleep_duration_minutes) : '--'}
            subvalue={wellness?.sleep_performance ? `${Math.round(wellness.sleep_performance)}% quality` : null}
          />
          <MetricRow
            icon={Flame}
            label="Strain"
            value={wellness?.strain ? wellness.strain.toFixed(1) : '--'}
            subvalue="/ 21"
          />
        </div>
      </div>
    </div>
  )
}

function MetricRow({ icon: Icon, label, value, subvalue, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
      <div style={{
        width: 36, height: 36, borderRadius: 'var(--radius-sm)',
        background: 'rgba(255, 255, 255, 0.05)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon size={18} className="text-tertiary" />
      </div>
      <div style={{ flex: 1 }}>
        <p className="text-tertiary" style={{ fontSize: '0.75rem' }}>{label}</p>
        <p style={{ fontWeight: 500, color: color || 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
          {value} {subvalue && <span className="text-tertiary" style={{ fontWeight: 400 }}>{subvalue}</span>}
        </p>
      </div>
    </div>
  )
}

function formatSleep(minutes) {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

// AI Insight Card
function AIInsightCard({ wellness }) {
  const recovery = wellness?.recovery_score || 0

  let insight, icon, color
  if (recovery >= 67) {
    insight = "Your cognitive performance is peaking. The next 2-3 hours are optimal for deep work and complex problem-solving."
    icon = Brain
    color = 'var(--accent-green)'
  } else if (recovery >= 34) {
    insight = "Moderate energy levels detected. Focus on steady-state tasks and avoid overexertion. Consider a short walk to boost alertness."
    icon = Target
    color = 'var(--accent-blue)'
  } else {
    insight = "Your body needs recovery. Prioritize rest, hydration, and light activities. Complex tasks can wait until you're restored."
    icon = Moon
    color = 'var(--accent-yellow)'
  }

  const Icon = icon

  return (
    <div className="glass-card" style={{
      padding: 'var(--space-lg)',
      background: `linear-gradient(135deg, ${color}10, transparent)`,
      borderColor: `${color}30`
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)' }}>
        <div style={{
          width: 40, height: 40, borderRadius: 'var(--radius-sm)',
          background: `${color}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0
        }}>
          <Sparkles size={20} style={{ color }} />
        </div>
        <div>
          <p style={{ fontWeight: 500, marginBottom: 'var(--space-xs)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <Icon size={16} style={{ color }} />
            AI-Powered Insight
          </p>
          <p className="text-secondary" style={{ fontSize: '0.875rem', lineHeight: 1.5 }}>
            {insight}
          </p>
        </div>
      </div>
    </div>
  )
}

// Break Reminder Component
function BreakReminder({ breakInterval, onIntervalChange }) {
  const [timeUntilBreak, setTimeUntilBreak] = useState(breakInterval * 60)
  const [isActive, setIsActive] = useState(true)
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (isActive && timeUntilBreak > 0) {
      intervalRef.current = setInterval(() => {
        setTimeUntilBreak(t => {
          if (t <= 1) {
            // Time for break!
            if (notificationsEnabled && 'Notification' in window) {
              new Notification('Time for a break! 🧘', {
                body: 'You\'ve been focused for a while. Take a moment to stretch and rest your eyes.',
                icon: '/favicon.ico'
              })
            }
            return breakInterval * 60 // Reset
          }
          return t - 1
        })
      }, 1000)
    }
    return () => clearInterval(intervalRef.current)
  }, [isActive, breakInterval, notificationsEnabled])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      setNotificationsEnabled(permission === 'granted')
    }
  }

  const resetTimer = () => {
    setTimeUntilBreak(breakInterval * 60)
  }

  return (
    <div className="glass-card" style={{ padding: 'var(--space-xl)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
        <Clock size={20} style={{ color: 'var(--accent-blue)' }} />
        <h3>Break Reminder</h3>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xl)' }}>
        {/* Timer Display */}
        <div style={{ textAlign: 'center' }}>
          <p className="text-tertiary" style={{ fontSize: '0.75rem', marginBottom: 'var(--space-xs)' }}>
            Next break in
          </p>
          <p style={{
            fontSize: '2.5rem',
            fontWeight: 600,
            fontFamily: 'var(--font-mono)',
            color: timeUntilBreak < 300 ? 'var(--accent-yellow)' : 'var(--text-primary)'
          }}>
            {formatTime(timeUntilBreak)}
          </p>
        </div>

        {/* Controls */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          {/* Interval Selector */}
          <div>
            <p className="text-tertiary" style={{ fontSize: '0.75rem', marginBottom: 'var(--space-xs)' }}>
              Break interval
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
              {[30, 45, 60].map(interval => (
                <button
                  key={interval}
                  className={`btn ${breakInterval === interval ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ padding: 'var(--space-xs) var(--space-md)', fontSize: '0.875rem' }}
                  onClick={() => {
                    onIntervalChange(interval)
                    setTimeUntilBreak(interval * 60)
                  }}
                >
                  {interval}m
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <button
              className="btn btn-ghost"
              onClick={() => setIsActive(!isActive)}
              style={{ padding: 'var(--space-sm)' }}
            >
              {isActive ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <button
              className="btn btn-ghost"
              onClick={resetTimer}
              style={{ padding: 'var(--space-sm)' }}
            >
              <RotateCcw size={18} />
            </button>
            <button
              className={`btn ${notificationsEnabled ? 'btn-primary' : 'btn-ghost'}`}
              onClick={requestNotificationPermission}
              style={{ padding: 'var(--space-sm)' }}
              title={notificationsEnabled ? 'Notifications enabled' : 'Enable notifications'}
            >
              {notificationsEnabled ? <Bell size={18} /> : <BellOff size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Quick Mindfulness */}
      <div style={{
        marginTop: 'var(--space-lg)',
        padding: 'var(--space-md)',
        background: 'rgba(139, 92, 246, 0.1)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid rgba(139, 92, 246, 0.2)'
      }}>
        <button className="btn btn-ghost" style={{
          width: '100%',
          justifyContent: 'flex-start',
          gap: 'var(--space-md)',
          color: 'var(--accent-purple)'
        }}>
          <Coffee size={18} />
          <span>Start 2-min mindfulness break</span>
          <ChevronRight size={16} style={{ marginLeft: 'auto' }} />
        </button>
      </div>
    </div>
  )
}

// Integrations Card
function IntegrationsCard({ whoopConnected, onConnectWhoop }) {
  const integrations = [
    { id: 'whoop', name: 'Whoop', icon: Watch, connected: whoopConnected, color: '#00A7E1' },
    { id: 'apple-health', name: 'Apple Health', icon: Heart, connected: false, color: '#FF2D55', comingSoon: true },
    { id: 'google-fit', name: 'Google Fit', icon: Activity, connected: false, color: '#4285F4', comingSoon: true },
    { id: 'garmin', name: 'Garmin', icon: Watch, connected: false, color: '#007CC3', comingSoon: true },
    { id: 'oura', name: 'Oura Ring', icon: Target, connected: false, color: '#A5A5A5', comingSoon: true },
    { id: 'fitbit', name: 'Fitbit', icon: Activity, connected: false, color: '#00B0B9', comingSoon: true }
  ]

  return (
    <div className="glass-card" style={{ padding: 'var(--space-xl)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
        <Link2 size={20} style={{ color: 'var(--accent-blue)' }} />
        <h3>Health Integrations</h3>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-md)' }}>
        {integrations.map(({ id, name, icon: Icon, connected, color, comingSoon }) => (
          <div
            key={id}
            style={{
              padding: 'var(--space-md)',
              background: connected ? `${color}10` : 'rgba(255, 255, 255, 0.02)',
              borderRadius: 'var(--radius-md)',
              border: `1px solid ${connected ? `${color}30` : 'var(--glass-border)'}`,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-md)'
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 'var(--radius-sm)',
              background: connected ? `${color}20` : 'rgba(255, 255, 255, 0.05)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Icon size={20} style={{ color: connected ? color : 'var(--text-tertiary)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 500, fontSize: '0.875rem' }}>{name}</p>
              {connected ? (
                <p style={{ fontSize: '0.75rem', color: 'var(--accent-green)' }}>Connected</p>
              ) : comingSoon ? (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>Coming Soon</p>
              ) : (
                <button
                  className="btn btn-ghost"
                  style={{ padding: 0, fontSize: '0.75rem', height: 'auto' }}
                  onClick={onConnectWhoop}
                >
                  Connect
                </button>
              )}
            </div>
            {connected && <CheckCircle2 size={16} style={{ color: 'var(--accent-green)' }} />}
          </div>
        ))}
      </div>
    </div>
  )
}

// Privacy Card
function PrivacyCard() {
  const [trackingEnabled, setTrackingEnabled] = useState(true)

  return (
    <div className="glass-card" style={{ padding: 'var(--space-xl)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
        <Shield size={20} style={{ color: 'var(--accent-green)' }} />
        <h3>Privacy & Data</h3>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-lg)' }}>
        <div>
          <p style={{ fontWeight: 500, marginBottom: 'var(--space-xs)' }}>Health Tracking</p>
          <p className="text-tertiary" style={{ fontSize: '0.875rem' }}>
            Enable real-time health data sync
          </p>
        </div>
        <button
          className={`toggle ${trackingEnabled ? 'active' : ''}`}
          onClick={() => setTrackingEnabled(!trackingEnabled)}
          style={{
            width: 48, height: 28, borderRadius: 14,
            background: trackingEnabled ? 'var(--accent-green)' : 'var(--glass-bg)',
            border: '1px solid var(--glass-border)',
            position: 'relative', cursor: 'pointer', transition: 'background 0.2s'
          }}
        >
          <div style={{
            width: 20, height: 20, borderRadius: '50%',
            background: 'white',
            position: 'absolute', top: 3,
            left: trackingEnabled ? 24 : 4,
            transition: 'left 0.2s'
          }} />
        </button>
      </div>

      <p className="text-tertiary" style={{ fontSize: '0.75rem', lineHeight: 1.5 }}>
        Your health data is encrypted and stored securely. We never share your personal health information with third parties.
        <a href="/privacy" style={{ color: 'var(--accent-blue)', marginLeft: 'var(--space-xs)' }}>
          View Privacy Policy →
        </a>
      </p>
    </div>
  )
}

// Main Health Page
export default function Health() {
  const { user } = useAuth()
  const [wellness, setWellness] = useState(null)
  const [whoopConnected, setWhoopConnected] = useState(false)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [breakInterval, setBreakInterval] = useState(45)

  useEffect(() => {
    loadHealthData()
  }, [])

  async function loadHealthData() {
    setLoading(true)
    try {
      const [statusRes, wellnessRes] = await Promise.all([
        fetch('/api/whoop/status'),
        fetch('/api/whoop/wellness')
      ])

      if (statusRes.ok) {
        const data = await statusRes.json()
        setWhoopConnected(data.connected)
      }
      if (wellnessRes.ok) {
        const data = await wellnessRes.json()
        setWellness(data.wellness)
      }
    } catch (err) {
      console.error('Failed to load health data:', err)
    } finally {
      setLoading(false)
    }
  }

  async function connectWhoop() {
    const res = await fetch('/api/auth/whoop/authorize')
    if (res.ok) {
      const data = await res.json()
      window.location.href = data.url
    }
  }

  async function syncHealth() {
    setSyncing(true)
    try {
      await fetch('/api/whoop/sync', { method: 'POST' })
      await loadHealthData()
    } finally {
      setSyncing(false)
    }
  }

  // Calculate overall health score
  const calculateHealthScore = () => {
    if (!wellness) return 50
    const recovery = wellness.recovery_score || 50
    const sleepQuality = wellness.sleep_performance || 50
    // Weighted average: 50% recovery, 30% sleep, 20% HRV trend
    return Math.round(recovery * 0.5 + sleepQuality * 0.3 + (wellness.hrv_rmssd ? Math.min(100, wellness.hrv_rmssd) : 50) * 0.2)
  }

  const healthScore = calculateHealthScore()

  if (loading) {
    return (
      <div className="page container" style={{ paddingTop: 'var(--space-2xl)' }}>
        <div className="animate-pulse text-secondary" style={{ textAlign: 'center' }}>
          Loading health data...
        </div>
      </div>
    )
  }

  return (
    <div className="page container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: 'var(--space-xl)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ marginBottom: 'var(--space-xs)' }}>Health Dashboard</h1>
            <p className="text-secondary">
              Monitor your wellbeing and optimize your performance
            </p>
          </div>
          {whoopConnected && (
            <button
              className="btn btn-secondary"
              onClick={syncHealth}
              disabled={syncing}
            >
              {syncing ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Activity size={16} />
                  Sync Now
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>

      {!whoopConnected ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="glass-card" style={{ padding: 'var(--space-2xl)', textAlign: 'center' }}>
            <div style={{
              width: 80, height: 80,
              margin: '0 auto var(--space-lg)',
              borderRadius: '50%',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Heart size={36} className="text-secondary" />
            </div>
            <h2 style={{ marginBottom: 'var(--space-sm)' }}>Connect Your Health Data</h2>
            <p className="text-secondary" style={{ marginBottom: 'var(--space-xl)', maxWidth: 400, margin: '0 auto var(--space-xl)' }}>
              Link your wearable device to get personalized insights and recommendations based on your recovery, sleep, and activity levels.
            </p>
            <IntegrationsCard whoopConnected={false} onConnectWhoop={connectWhoop} />
          </div>
        </motion.div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-lg)' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <HealthPulseCard wellness={wellness} healthScore={healthScore} />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <AIInsightCard wellness={wellness} />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <BreakReminder breakInterval={breakInterval} onIntervalChange={setBreakInterval} />
            </motion.div>
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <IntegrationsCard whoopConnected={whoopConnected} onConnectWhoop={connectWhoop} />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <PrivacyCard />
            </motion.div>
          </div>
        </div>
      )}
    </div>
  )
}
